/* eslint-disable no-unused-vars */
import React from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudHostingIllustration from 'svg/illustrations/CloudHosting';

const Hero = () => {
    const theme = useTheme();
    const isMd = useMediaQuery(theme.breakpoints.up('md'), {
        defaultMatches: true,
    });

    return (
        <Grid container spacing={4}>
            <Grid
                item
                container
                alignItems={'center'}
                justifyContent={'center'}
                xs={12}
                md={7}
            >
                <Box 
                    component="section"
                    data-aos={isMd ? 'fade-right' : 'fade-up'} 
                    paddingY={{ xs: 6, md: 10 }}
                    sx={{
                        position: 'relative',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '-20px',
                            left: '-20px',
                            width: '120px',
                            height: '120px',
                            background: `radial-gradient(circle, ${theme.palette.primary.main}25, ${theme.palette.primary.light}15, transparent)`,
                            borderRadius: '50%',
                            zIndex: 0,
                            filter: 'blur(1px)',
                        },
                    }}
                >
                    <Box 
                        marginBottom={3}
                        sx={{
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <Typography
                            component="h1"
                            variant="h1"
                            color="textPrimary"
                            sx={{
                                fontWeight: 900,
                                fontSize: { xs: '2.75rem', md: '4rem', lg: '4.5rem' },
                                lineHeight: 1.1,
                                letterSpacing: '-0.03em',
                                position: 'relative',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: '-10px',
                                    left: 0,
                                    width: '80px',
                                    height: '4px',
                                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, transparent)`,
                                    borderRadius: '2px',
                                },
                            }}
                        >
                            Enterprise Technology Solutions{' '}
                            <Box
                                component="span"
                                sx={{
                                    color: theme.palette.primary.main,
                                    position: 'relative',
                                    display: 'inline-block',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        bottom: '4px',
                                        left: 0,
                                        right: 0,
                                        height: '8px',
                                        background: `${theme.palette.primary.main}20`,
                                        zIndex: -1,
                                    },
                                }}
                            >
                                Built for Scale
                            </Box>
                        </Typography>
                    </Box>
                    <Box marginBottom={4}>
                        <Typography
                            variant="h5"
                            component="p"
                            color="textSecondary"
                            sx={{
                                fontSize: { xs: '1.1rem', md: '1.25rem' },
                                lineHeight: 1.7,
                                maxWidth: '90%',
                            }}
                        >
                            We deliver secure, scalable, and innovative technology solutions for government and enterprise clients. 
                            From custom software and AI to cybersecurity and cloud migration, we specialize in mission-critical systems 
                            that drive efficiency, compliance, and digital transformation.
                        </Typography>
                    </Box>
                    <Box 
                        display="flex" 
                        flexDirection={{ xs: 'column', sm: 'row' }} 
                        gap={2}
                        marginBottom={4}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            component={Link}
                            to="/page-contact"
                            sx={{
                                paddingX: 4,
                                paddingY: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: 2,
                            }}
                        >
                            Schedule a Consultation
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            size="large"
                            onClick={() => {
                                const servicesSection = document.getElementById('services-section');
                                if (servicesSection) {
                                    servicesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }
                            }}
                            sx={{
                                paddingX: 4,
                                paddingY: 1.5,
                                fontSize: '1.1rem',
                                fontWeight: 600,
                                textTransform: 'none',
                                borderRadius: 2,
                            }}
                        >
                            View Our Services
                        </Button>
                    </Box>
                    <Box 
                        display="flex" 
                        flexWrap="wrap" 
                        gap={3}
                        sx={{
                            '& > *': {
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }
                        }}
                    >
                        <Box>
                            <SecurityIcon color="primary" />
                            <Typography variant="body1" fontWeight={600}>
                                Enterprise Security
                            </Typography>
                        </Box>
                        <Box>
                            <VerifiedUserIcon color="primary" />
                            <Typography variant="body1" fontWeight={600}>
                                Compliance Ready
                            </Typography>
                        </Box>
                        <Box>
                            <CloudDoneIcon color="primary" />
                            <Typography variant="body1" fontWeight={600}>
                                Cloud-Native
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Grid>
            <Grid item xs={12} md={5}>
                <Box
                    height={'100%'}
                    width={'100%'}
                    display={'flex'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    paddingY={{ xs: 4, md: 8 }}
                    data-aos={isMd ? 'fade-left' : 'fade-up'}
                >
                    <Box 
                        height={'100%'} 
                        width={'100%'} 
                        maxHeight={600}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Box
                            component={CloudHostingIllustration}
                            width={'100%'}
                            height={'100%'}
                            aria-label="Cloud hosting and enterprise technology illustration"
                        />
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
};

export default Hero;
